/**
 * Share wording across the linear flow, guided setup, and dev gallery so gallery edits also reach user-
 * facing screens.
 */

import { i18n } from "~vue/i18n";

export interface ThinkingScreenConfig {
    /** Selects a process illustration; omit when showing result skeletons. */
    art?: "week" | "wheel" | "discs" | "marks";
    label: string;
    /** Only shown in skeleton mode; illustrated screens ignore status. */
    status?: string;
    stages: string[];
    skeleton?: "readings";
}

export type ThinkingWait = "scan" | "perspective" | "readings" | "profile";

/*
 * Getters, so each host's `v-bind="THINKING_SCREENS.scan"` resolves the wording in the active locale at
 * render time instead of at module load, and no host had to change shape.
 */
export const THINKING_SCREENS: Record<ThinkingWait, ThinkingScreenConfig> = {
    /* Both live hosts override the label with the run's own, which names the window
       actually read and changes wording once the scan has overrun. */
    get scan() {
        const { t } = i18n.global;
        return {
            art: "week" as const,
            label: t("onboarding.calendarFirst.thinking.calendar.label"),
            stages: [
                t("onboarding.calendarFirst.thinking.calendar.stages.pulling"),
                t("onboarding.calendarFirst.thinking.calendar.stages.people"),
                t("onboarding.calendarFirst.thinking.calendar.stages.writing"),
            ],
        };
    },
    /* The wheel cycles the sixteen type names as it turns, which is this screen's progress signal; a
       stage line underneath it would be a third block of text saying the same thing more slowly. */
    get perspective() {
        const { t } = i18n.global;
        return {
            art: "wheel" as const,
            label: t("onboarding.calendarFirst.thinking.perspective.label"),
            stages: [],
        };
    },
    /* The longest wait in the flow, so it gets a fourth line: three cover barely half of it. The discs
       overlapping is the calendar and the answers being read together, which the title says too. */
    get readings() {
        const { t } = i18n.global;
        return {
            art: "discs" as const,
            label: t("onboarding.calendarFirst.thinking.readings.label"),
            stages: [
                t("onboarding.calendarFirst.thinking.readings.stages.matching"),
                t("onboarding.calendarFirst.thinking.readings.stages.rereading"),
                t("onboarding.calendarFirst.thinking.readings.stages.writing"),
                t("onboarding.calendarFirst.thinking.readings.stages.private"),
            ],
        };
    },
    /* The profile's own six section marks, so the illustration is the document being written. Losing the
       skeletons loses the lede with them, and its promise that nothing is published yet is the fourth
       stage line instead. */
    get profile() {
        const { t } = i18n.global;
        return {
            art: "marks" as const,
            label: t("onboarding.calendarFirst.thinking.profile.label"),
            stages: [
                t("onboarding.calendarFirst.thinking.profile.stages.gathering"),
                t("onboarding.calendarFirst.thinking.profile.stages.drafting"),
                t("onboarding.calendarFirst.thinking.profile.stages.checking"),
                t("onboarding.calendarFirst.thinking.profile.stages.private"),
            ],
        };
    },
};
